import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, onlineManager } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { queryPersister, shouldPersistQuery, wipePersistedQueryCacheForSignOut } from '@/lib/queryPersist'
import { decodeStoredClaims } from '@/lib/offlineSession'
import { isPairedDevice } from '@/lib/deviceSession'
import { OLD_APP_HOST, decideOldAddressMove } from '@/lib/oldAddressMove'
import { pendingCount } from '@/lib/offlineQueue/db'
import OldAddressNotice from '@/components/OldAddressNotice'
import { supabase } from '@/lib/supabase'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom'
import { createTransitionHistory } from '@/lib/pageTransition'
import * as Sentry from '@sentry/react'
import App from './App'
import ErrorBoundary from '@/components/ErrorBoundary'
import { installGlobalErrorHandlers, reportError, fireUnderweightAlertIfMatch } from '@/lib/errorReporter'
import { sentryBeforeSend, sentryEnabled } from '@/lib/sentryBeforeSend'
import { installPreloadErrorReload } from '@/lib/lazyWithReload'
import { installQueueFlusher, onQueueChanged } from '@/lib/offlineQueue'
import { installSessionWatchdog, healSessionAfterWake } from '@/lib/sessionWatchdog'
import { installGetSessionHeal } from '@/lib/getSessionHeal'
import { installUiTelemetry, trackMutationError, trackQueryError } from '@/lib/uiTelemetry'
import { noteFailedSave } from '@/lib/helpNudge'
import { installNumberInputWheelGuard } from '@/lib/numericInput'
import { unwedgeOnlineManager, createProbeUnwedge, installTokenRefreshHeal, createWakeHeal, createSleepDetector } from '@/lib/livenessHeals'
import { markLinkProven } from '@/lib/offlineQueue/classify'
import { drainQueue } from '@/lib/offlineQueue'
import { hardenPrototypes } from '@/lib/hardenPrototypes'
import { scheduleMapboxCacheSweep } from '@/lib/mapboxCacheSweep'
import './index.css'

// BrowserRouter has no view-transition hook, so the router runs on a history
// whose listen() wraps each update in a page slide (src/lib/pageTransition.ts).
// Do NOT add the v7_startTransition future flag: it wraps the router's setState
// in React.startTransition, which the slide's flushSync cannot force.
const pageHistory = createTransitionHistory()

// Prototype-pollution hardening (sec audit medium #2). preventExtensions, NOT
// Object.freeze — freeze breaks lazily-imported pdf-lib. Rationale + the
// incident that forced the change live in the module; the invariant is locked
// by hardenPrototypes.test.ts. Runs after static module init.
hardenPrototypes()

// Recovery escape hatch — when an operator visits ?reset-sw=1 we
// unregister every service worker for this origin, delete every
// cache, then reload to a clean state. Used when a SW upgrade goes
// half-installed and the PWA can't recover on its own (observed on
// Samsung Chrome 2026-05-19). Runs BEFORE Sentry init / React mount
// so a broken SW that's preventing the bundle from booting still
// gets cleared. Self-contained — no app code dependencies.
if (typeof window !== 'undefined' && /[?&]reset-sw=1\b/.test(window.location.search)) {
  void (async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map(r => r.unregister()))
      }
      if (typeof caches !== 'undefined') {
        const names = await caches.keys()
        await Promise.all(names.map(n => caches.delete(n)))
      }
    } catch { /* best-effort */ }
    // Replace history entry so a refresh doesn't loop through the
    // reset path forever.
    window.location.replace(window.location.origin + '/')
  })()
}

// Initialise Sentry first — when a DSN is configured at build time. Skipping
// init() means Sentry.captureException becomes a no-op, so the rest of the
// reporter still works in environments without Sentry.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

const SENTRY_ENABLED = sentryEnabled(
  import.meta.env.PROD,
  typeof location !== 'undefined' ? location.hostname : undefined,
)

if (SENTRY_DSN) {
  Sentry.init({
    dsn:                SENTRY_DSN,
    // ONE gate: a real deployed app, never a dev server or an e2e harness on
    // someone's laptop (which is how 203 "e2e injected failure" events reached
    // the production project). `enabled: false` still lets every Sentry.* call
    // be a safe no-op — nothing leaves the machine. Rationale + the measured
    // numbers live on sentryEnabled().
    enabled:            SENTRY_ENABLED,
    // Vercel tells the build which deployment this is: 'production' for
    // app.kf49citrus.com, 'preview' for a PR deploy, 'development' for
    // `vercel dev`. vite.config.ts defines it the same way it defines
    // VITE_APP_VERSION. Falling back to MODE keeps non-Vercel builds honest.
    // This is what makes an `environment equals production` filter on the
    // Sentry alert rule mean something — previews used to arrive tagged
    // `production` because MODE is 'production' for ANY `vite build`.
    environment:        import.meta.env.VITE_VERCEL_ENV || import.meta.env.MODE,
    release:            import.meta.env.VITE_APP_VERSION,
    // Trace 10% of sessions in prod (catches perf issues without ballooning
    // the Sentry quota); always trace in dev so devs see a populated UI.
    tracesSampleRate:   import.meta.env.PROD ? 0.1 : 1.0,
    // Replay only on errors, with a small ratio of healthy sessions for
    // baseline. Stays at 1.0: a beforeSend-dropped event never flushes a
    // replay, so the quota burn is fixed at source rather than by sampling
    // the signal down too.
    replaysOnErrorSampleRate:   1.0,
    replaysSessionSampleRate:   0.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Drop what the reporter drops (ONE list — isSwallowed), and fingerprint
    // a database error by code + message family so one defect is one issue.
    // The whole rationale, and the tests, live in the module.
    beforeSend: sentryBeforeSend,
  })

  // Tag EVERY Sentry event — errors, performance traces, session replays —
  // with the signed-in user, so an issue shows *who* hit it, not just an
  // opaque id. errorReporter also sets user on the exceptions it forwards;
  // this is the wider net that also covers traces/replays that never go
  // through reportError. onAuthStateChange fires an INITIAL_SESSION event on
  // subscribe, so this also tags the already-logged-in reload case without a
  // separate getSession() call (which would contend for the auth Web-Lock at
  // boot — see the getSession-wedge note below). Cleared to null on sign-out.
  // POPIA note: this sends the operator's login email to Sentry (internal
  // staff address; masked-text Replay is already on). Drop the email field
  // here + in errorReporter if that ever needs revisiting.
  supabase.auth.onAuthStateChange((_evt, session) => {
    const user = session?.user
    Sentry.setUser(user ? { id: user.id, email: user.email ?? undefined } : null)
  })
}

// Every way out of a session (Sign out, a revoked or rejected refresh, the
// admin sign-out) emits SIGNED_OUT; not all of them pass signOutEverywhere.
// The persisted cache carries the face roster, so seal and wipe it on each.
// A paired station keeps its roster on purpose (signOutThisDevice).
supabase.auth.onAuthStateChange((evt) => {
  if (evt === 'SIGNED_OUT' && !isPairedDevice()) void wipePersistedQueryCacheForSignOut()
})

// Forward uncaught window errors + promise rejections to the client_errors
// sink so admins can triage in-app without a phone call. (Sentry has its own
// global handlers; this layer is the always-on baseline.)
installGlobalErrorHandlers()

// Global net for Vite preload failures (stale-bundle chunk/CSS 404s after a
// deploy) across every dynamic import, routed through the same loop-guarded
// reload as lazyWithReload. Catches the non-precached mapbox-gl CSS preload
// that bit /farm/sprays (Sentry a01feabf). See src/lib/lazyWithReload.ts.
installPreloadErrorReload()

// Background flusher for the offline queue (tipping_log inserts that were
// captured during a Wi-Fi outage). Drains automatically when back online.
installQueueFlusher()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // 30s before refetch
      gcTime:    7_200_000,     // 2hr cache for offline window
      // One retry for ordinary failures — but NOT for timeouts: that request
      // already waited its full ceiling on what's almost certainly a
      // black-holed connection, and a retry doubles the spinner for nothing.
      // Fail visibly instead; reconnect/auth-heal/mount refetches recover.
      // Matches the raw DOMException (name), postgrest-js's wrapped
      // plain-object form (message "AbortError: Request timed out …"),
      // withTimeout's ValidationError ("… took too long …" — the idle-wake
      // audit found it matched neither pattern, so the tenant switcher's 30s
      // timeout was silently retried into a ~61s blank window), and the
      // anon-guard block ("blocked: auth refresh pending" — it already ran a
      // bounded heal attempt before failing; the auth-heal invalidate refetches
      // when the session actually recovers).
      retry: (failureCount, error) => {
        const e = error as { name?: string; message?: string } | null
        if (e?.name === 'TimeoutError' || e?.name === 'AbortError') return false
        if (typeof e?.message === 'string' &&
            /^AbortError:|timed out|took too long|blocked: auth refresh pending/i.test(e.message)) return false
        return failureCount < 1
      },
      // Operator-tool default: every time a page (or component with a
      // useQuery) mounts, kick off a background refetch even if
      // staleTime says the data is still "fresh." Cached data is shown
      // immediately so there's no spinner / flicker; the UI just
      // self-heals when the fresh response lands a moment later.
      //
      // Without this, 29+ queries that explicitly set `staleTime: 5min`
      // (config, lookups, letterhead, …) stayed frozen for the whole
      // 5-min window even after another tab / page / operator wrote
      // new data. Operators reported "must reload to see the new row"
      // across many pages. This + the existing mutation invalidations
      // give the desired live-feeling UI.
      refetchOnMount:        'always',
      refetchOnReconnect:    'always',
      // Was `true`. On a packhouse/farm tablet constantly switched between the
      // app and scanner/camera apps, focus-refetch re-ran EVERY mounted query
      // on every alt-tab — a standing-load storm that compounded with each
      // page's refetchInterval. It's also largely redundant with the liveness
      // we keep: refetchOnMount:'always' refreshes on navigation (the cross-
      // operator "see the new row" path), per-list pollers keep live boards
      // current, and refetchOnReconnect heals network blips. Off = the storm
      // goes away with no regression on those paths. (DB-efficiency audit, batch 1.)
      refetchOnWindowFocus:  false,
    },
    mutations: {
      // CRITICAL for the offline queue. TanStack v5's default networkMode
      // ('online') PAUSES a mutation while onlineManager reports offline:
      // .mutate() flips isPending (the form shows its spinner) but the
      // mutationFn NEVER RUNS until the 'online' event — so the offline-queue
      // handoff inside the mutationFn (insertOrQueueScouting & co.) was
      // unreachable in airplane mode. Field symptom (2026-06-12, /farm/
      // scouting): save a scout offline → spinner forever, no error, nothing
      // queued; on reconnect the paused mutation finally fired and the row
      // landed/queued. The walk save was immune only because it's a plain
      // async fn, not a useMutation — that asymmetry was the tell.
      //
      // Weak-signal (the common field case) masked this for months:
      // navigator.onLine stays TRUE on a dead link, mutations run, the
      // reachability probe fails → queued. True airplane-mode offline was the
      // one path that paused.
      //
      // 'always' = mutationFns always execute. Queue-backed saves do their
      // offline() short-circuit and return 'queued' instantly. Non-queue
      // mutations attempted offline now fail FAST ("Failed to fetch" surfaced
      // in the form) instead of spinning silently until reconnect — the right
      // trade for an operator tool, and the error reporter already filters
      // those messages as transient noise.
      networkMode: 'always',
    },
  }
})

// React Query swallows query/mutation errors at the hook level — they don't
// bubble to error boundaries. Subscribe globally so silent background
// failures still land in the error log.
queryClient.getQueryCache().subscribe((ev) => {
  if (ev.type === 'updated' && ev.action.type === 'error') {
    // Failed READS are friction too — a screen that can't load its data
    // previously scored zero in ui_friction_summary. trackQueryError
    // throttles per screen+label and drops transient network noise.
    trackQueryError(ev.action.error, ev.query.queryKey)
    void reportError(ev.action.error, {
      source: 'manual',
      context: { kind: 'query', queryKey: ev.query.queryKey },
    })
  }
})
queryClient.getMutationCache().subscribe((ev) => {
  if (ev.type === 'updated' && ev.action.type === 'error') {
    // Alert configured recipients if this was a blocked-underweight pallet save
    // (mig 715). Runs alongside the report so it catches every save path.
    fireUnderweightAlertIfMatch(ev.action.error)
    // BEFORE reportError: reportError swallows ValidationErrors as operator
    // noise, but for UX telemetry a validation failure is exactly the
    // friction signal we want per-screen.
    trackMutationError(ev.action.error, ev.mutation?.options?.mutationKey)
    // Two failed saves on one page offer "Stuck? Talk to support" (helpNudge.ts).
    noteFailedSave()
    void reportError(ev.action.error, {
      source: 'manual',
      context: { kind: 'mutation' },
    })
  }
})

// Self-heal open lists after the offline queue drains. A write made while
// offline (or behind a captive portal) is queued in IndexedDB and lands in the
// DB later via the background flusher — but nothing told React Query, so the
// list the operator is staring at stayed stale until a manual reload or the
// next foreground poll. Symptom (2026-06-04, /farm/scouting): a scout saved an
// observation, it synced, but never appeared on his device while it showed on
// everyone else's. `notifyChanged` fires on enqueue + drain + discard (never on
// live sends), so gating on `navigator.onLine` narrows this to the drain case —
// when rows have actually reached the server — and refetches every active query
// so the freshly-synced rows appear without a reload.
onQueueChanged(() => {
  if (navigator.onLine) void queryClient.invalidateQueries()
})

// Proactive JWT refresh on tab focus / boot. Fixes the symptom where a
// tab left open overnight came back with an expired JWT, every Supabase
// query 401-ed, and the UI showed empty cards until the operator
// manually reloaded every page. See sessionWatchdog.ts for the full
// rationale.
installSessionWatchdog(queryClient)

// getSession-wedge self-heal: supabase-js resolves every request's bearer via
// auth.getSession(), which serialises behind auth-js's in-process lock. When that
// lock deadlocks (visibilitychange recover racing a cold-detail query burst), the
// whole app freezes on a loading spinner forever and only a manual reload fixes
// it. This watchdog confirms the wedge (getSession unresponsive >45s while a valid
// token sits on disk) and does the guarded reload automatically. See
// getSessionHeal.ts for the full rationale + every safety guard. (PR #173.)
installGetSessionHeal()

// ── Staleness heals (2026-06-11 root-cause fixes) ──────────────────────────
// Auth-heal invalidate: whenever the JWT actually refreshes (supabase-js's
// internal ticker — the watchdog's direct-REST path invalidates on its own),
// refetch active queries. Queries that errored or returned RLS-empty
// "successes" during a dead-token window repopulate without a reload.
// TOKEN_REFRESHED fires roughly hourly, so this costs one refetch sweep per
// hour — far below the per-alt-tab storm that got refetchOnWindowFocus
// turned off.
installTokenRefreshHeal(
  (cb) => { supabase.auth.onAuthStateChange((evt) => cb(evt)) },
  () => { void queryClient.invalidateQueries() },
)

// Wake heal: returning to a device that was hidden/asleep for a while is the
// liveness gap left by refetchOnWindowFocus:false — pollers skip hidden
// ticks, realtime sockets die without event replay, and refetchOnReconnect
// only fires if the browser SAW an offline→online transition. So on every
// return to visible: UN-WEDGE react-query's onlineManager (its flag only
// moves on window online/offline events; missing the 'online' event while
// the tab was frozen wedges every query and mutation in 'paused' forever),
// and after a real absence refetch active queries once. Duration-gated so
// scanner/camera alt-tabs (seconds) cost nothing — the refetch storm that
// killed focus-refetch stays dead.
//
// Un-wedge ONLY — never force the flag FALSE from navigator.onLine. The
// first ship of this heal did `setOnline(navigator.onLine)` and promptly
// reproduced the bug it existed to fix (2026-06-11, Jason's Runs page):
// navigator.onLine reads false during wake-time Wi-Fi reassociation (and
// chronically on some VPN/virtual-interface setups), so the sync PAUSED
// every query — fresh navigations sat on an eternal spinner with nothing
// on the wire, healed only by a reload (fresh JS defaults the flag true).
// This codebase already documents navigator.onLine as untrustworthy
// (sessionWatchdog, swRecovery); only the un-wedge direction may trust it.
// Genuine offline keeps being handled by the browser's own events.
const wakeHeal = createWakeHeal({
  now: () => Date.now(),
  invalidate: () => { void queryClient.invalidateQueries() },
})
document.addEventListener('visibilitychange', () => {
  const vis = document.visibilityState
  if (vis === 'visible' && navigator.onLine) onlineManager.setOnline(true) // un-wedge on return
  wakeHeal(vis)
})

// Sleep detector (2026-06-12): the wake heal above is keyed on
// visibilitychange — which NEVER FIRES when a desktop display sleeps with
// the app window frontmost (the page was "visible" the whole time), and is
// unreliable across some tablet doze modes. Reality check from the reload
// telemetry (first morning of data): the heal existed, the operator still
// reloaded — pollers resume up to a full interval after wake, realtime
// takes ~30s+ to reconnect, and a human F5s stale numbers within seconds.
// A frozen timer is the one signal every sleep variant shares: this 15s
// heartbeat measures its own delay, and a gap ≥90s means the machine/tab
// was suspended — so heal IMMEDIATELY (the coalesced tick fires right on
// wake): un-wedge the online flag and refetch everything active once.
const SLEEP_TICK_MS = 15_000
const sleepDetector = createSleepDetector({
  now: () => Date.now(),
  tickMs: SLEEP_TICK_MS,
  // A ≥90s freeze = the machine/tab was suspended → un-wedge, heal the
  // TOKEN, then refetch once. Order is load-bearing (idle-wake audit H4):
  // the invalidate used to fire immediately, racing the refetch burst up to
  // 60s ahead of the watchdog's own heal — benign on a healthy network,
  // wedge-feeding on a bad one. healSessionAfterWake resolves instantly when
  // the stored token isn't near expiry, and is the watchdog's single-flight
  // bounded refresh otherwise; the invalidate runs either way.
  onFreeze: () => {
    if (navigator.onLine) onlineManager.setOnline(true)
    void healSessionAfterWake().finally(() => { void queryClient.invalidateQueries() })
  },
})
setInterval(() => {
  // onlineManager wedge guard (2026-06-16): queries run at the default
  // networkMode 'online' (only mutations are 'always'), so if onlineManager
  // gets stuck reporting offline — a spurious 'offline' event or a MISSED
  // 'online' event on roaming packhouse Wi-Fi, while the device stays AWAKE
  // and VISIBLE — every query and poller is paused, invalidateQueries() is a
  // no-op, and the wake/sleep heals can't help (no return-to-visible, no ≥90s
  // freeze). Reconcile every 15s: if the OS reports online but onlineManager
  // disagrees, un-wedge so the next poll/sweep actually fetches. Un-wedge ONLY
  // (never force FALSE — see unwedgeOnlineManager / the 2026-06-11 regression).
  if (unwedgeOnlineManager(onlineManager, navigator.onLine)) {
    void queryClient.invalidateQueries()
  }
  void probeUnwedge()
  sleepDetector()
}, SLEEP_TICK_MS)

// Probe-verified un-wedge (idle-wake audit 6.1/P2.8) — covers the one state
// the reconciler above can't: navigator.onLine stuck FALSE (VPN/virtual-NIC
// fleet devices) while the link actually works. After 2 consecutive
// both-signals-offline ticks (~30s), fire ONE cache-busted probe; ANY HTTP
// response (even a 404) proves the link → un-wedge + refetch + drain queued
// writes (which trust the probe over navigator.onLine for 30s via
// markLinkProven). A failed probe never forces the flag false. The probe URL
// matches no SW route (not precached, not a navigation, not the Supabase
// API), so Workbox lets it hit the real network — and unlike
// swRecovery.networkReachable() it does NOT short-circuit on
// navigator.onLine === false, which is exactly the lying state it exists for.
const probeUnwedge = createProbeUnwedge({
  om: onlineManager,
  navigatorOnline: () => navigator.onLine,
  probe: async () => {
    try {
      const ctrl = new AbortController()
      const timer = setTimeout(() => ctrl.abort(), 5_000)
      await fetch(`/__net_probe__?t=${Date.now()}`, { method: 'HEAD', cache: 'no-store', signal: ctrl.signal })
      clearTimeout(timer)
      return true
    } catch { return false }
  },
  onUnwedge: () => {
    markLinkProven()
    void queryClient.invalidateQueries()
    void drainQueue()
  },
})

// Visible-staleness sweep (2026-06-12): the last residual of the
// refetchOnWindowFocus:false decision — a screen the operator PARKS ON
// while the device stays awake. ~65 screens have no poller and no realtime
// (e.g. /financial, reloaded by an operator on day one of the telemetry);
// their only heals were navigation and the ~hourly TOKEN_REFRESHED sweep.
// Every minute, refetch ACTIVE queries whose data (or error) is older than
// 5 minutes. Load-bounded by construction: a query a poller already keeps
// fresh is never 5 minutes stale (untouched), so this only ticks the
// parked long-tail — worst case each active query refetches once per 5
// minutes per device, far below the per-alt-tab focus storm that got
// focus-refetch disabled. Errored queries get the same once-per-5-min
// retry, which finally gives error-poisoned parked screens a recovery
// path that doesn't require a remount.
const SWEEP_MAX_AGE_MS = 5 * 60_000
setInterval(() => {
  if (document.visibilityState !== 'visible' || !navigator.onLine) return
  const cutoff = Date.now() - SWEEP_MAX_AGE_MS
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const s = q.state
      if (s.fetchStatus !== 'idle') return false
      if (s.status === 'error') return s.errorUpdatedAt < cutoff
      return s.dataUpdatedAt > 0 && s.dataUpdatedAt < cutoff
    },
  })
}, 60_000)

// One-shot cleanup: pre-2026-06-11 service workers cached PostgREST responses
// in 'supabase-api-cache' (see vite.config.ts for why that rule was removed —
// it served up-to-2h-old rows as fresh 200s on any network flake). Delete the
// orphaned bucket; no-ops once gone.
if (typeof caches !== 'undefined') void caches.delete('supabase-api-cache').catch(() => {})

// Anonymous UI telemetry (mig 805): buffer + flush timers, tab-hide flush,
// carryover resend. Route changes are tracked from App.tsx; this just
// installs the plumbing. Per-device off-switch:
// localStorage.setItem('kf49_ui_telemetry', 'off').
installUiTelemetry()

// =============================================================================
// Global guard for <input type="number"> — mouse-wheel scroll while a number
// input is focused silently decrements/increments the value by ±1 per scroll
// click, with no visual cue. KF49 hit this in production 2026-05-13:
// operators logged "17 bins" on the Rejects page and sometimes saved 16
// because the page scroll moved through a focused number input.
//
// The guard itself lives in src/lib/numericInput.ts (installNumberInputWheelGuard)
// so it is unit-testable (REL-054); new inputs should use the safeDigits /
// safeDecimal helpers there, but this guard covers every existing type="number"
// call site in one shot.
// =============================================================================
installNumberInputWheelGuard()

// Mapbox Product Terms cl 2.8.1: no Mapbox content cached on the device past 30
// days from download. The service worker refuses + deletes a stale entry when
// it is READ; this daily, idle-time sweep deletes the ones nobody reads again
// (src/lib/mapboxCacheSweep.ts). Never blocks start-up, never throws.
scheduleMapboxCacheSweep()

function renderApp(): void {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        {/* Durable read cache (offline-first). Restores the last query cache from
            IndexedDB on boot so forms have their dropdowns cold-offline, then
            refetchOnMount:'always' heals everything online. Only successful
            queries are persisted; a 24h maxAge + the app-version buster bound
            staleness, and the stale-schema recovery wipes it on a breaking deploy. */}
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: queryPersister,
            maxAge:    24 * 60 * 60 * 1000,
            // Suffix force-invalidates every device's persisted cache ONCE to
            // evict caches poisoned by the JSON-serialisation bug guarded below:
            // a query holding a Map was persisted as `{}` (JSON can't serialise a
            // Map) and rehydrated without its prototype, so a consumer calling
            // `.has()`/`.get()` threw at render and tripped the top-level
            // ErrorBoundary — bricking the device on the app until the 24h maxAge.
            // (Sentry 6c2579ef — /labour, 2026-06-04.) `.rq2` evicted the original
            // top-level-Map poison; `.rq3` re-evicts now that we also refuse
            // NESTED Map/Set + Date (which a `.rq2` cache could still hold). Bump
            // the suffix again only if a future change needs another forced reset.
            // Since 2026-06-11 VITE_APP_VERSION is the git SHA (vite.config.ts),
            // so the buster finally rotates per deploy as this comment always
            // assumed — a persisted cache written by one build can no longer
            // rehydrate into a different build. (It was a static '1.0.0'.)
            //
            // Also tenant-scoped, via the active tenant id from the stored JWT
            // (decoded even when expired, so it works offline). The version alone
            // is the SAME for every tenant within one deploy, so on a tenant SWITCH
            // (always a reload) — or any reload while a different tenant's snapshot
            // is on disk — the persisted ['active-tenant'] + dashboard queries
            // rehydrated with isLoading:false and the gates + Dashboard + brand
            // painted the PREVIOUS tenant's app (e.g. farm flashing inside
            // exporter) until refetchOnMount:'always' healed it. Folding the tenant
            // id in means a tenant change mismatches the buster, so
            // PersistQueryClientProvider DISCARDS the stale cache instead of
            // restoring it — no cross-tenant flash. Within one tenant the buster is
            // stable, so field devices keep their offline cache across reloads.
            // 'anon' covers the pre-auth boot.
            buster:    (import.meta.env.VITE_APP_VERSION ?? 'dev') + '.rq3.'
                         + (decodeStoredClaims()?.active_tenant_id ?? 'anon'),
            dehydrateOptions: {
              // Persist a query only when it's successful AND its data round-trips
              // through JSON with no type loss (no Map/Set/Date/class-instance/cycle
              // anywhere — see isPersistSafe). Deep-checked, so a nested Map can't
              // slip through and brick the device on rehydration.
              // F114: the refusal used to be silent — "Prepare for offline"
              // reported success while a lookup went unsaved, and the cold
              // boot in the orchard met an empty picker with nothing to
              // explain it. The predicate is the only place that knows BOTH
              // the verdict and the key, so it is where the note belongs.
              // The rule (and the meta.noPersist opt-out) is shouldPersistQuery.
              shouldDehydrateQuery: shouldPersistQuery,
            },
          }}
        >
          <HistoryRouter history={pageHistory}>
            <App />
          </HistoryRouter>
        </PersistQueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  )
}

// The pre-rebrand address hands people over to bigagri.app once this phone has
// nothing waiting to upload (src/lib/oldAddressMove.ts). Every other host
// renders at once; the old host waits for one IndexedDB count. A promise, not a
// top-level await: the floor's Chrome 86 handsets can't parse one.
if (window.location.hostname === OLD_APP_HOST) {
  void decideOldAddressMove({
    hostname:     window.location.hostname,
    pathname:     window.location.pathname,
    search:       window.location.search,
    hash:         window.location.hash,
    online:       navigator.onLine,
    pairedDevice: isPairedDevice,
    readLocal:    k => { try { return window.localStorage.getItem(k) } catch { return null } },
    localKeys:    () => Object.keys(window.localStorage),
    queueCount:   pendingCount,
  }).then(
    d => {
      if (!d.move) { renderApp(); return }
      createRoot(document.getElementById('root')!).render(<OldAddressNotice target={d.target} />)
    },
    () => renderApp(),
  )
} else {
  renderApp()
}
